import { z } from 'zod';

import { realtimeClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';
import { ROLE_TYPES } from '@/lib/rbac/roles';

/**
 * Role strings the realtime `POST /sessions/:id/call/join` endpoint can
 * return. Sourced from the canonical role enum in `src/lib/rbac/roles.ts`
 * so new roles (e.g. volunteer added a690359) are accepted by default
 * and we don't have a silent FE/BE divergence again.
 */
export const backendRoleSchema = z.enum(ROLE_TYPES);

export type BackendRole = z.infer<typeof backendRoleSchema>;

export const joinCallResponseSchema = z.object({
  token: z.string().min(1),
  roomName: z.string().min(1),
  role: backendRoleSchema,
  livekitUrl: z.string().min(1),
});

export type JoinCallResponse = z.infer<typeof joinCallResponseSchema>;

export const startRecordingResponseSchema = z.object({
  egressId: z.string().min(1),
});

export type StartRecordingResponse = z.infer<typeof startRecordingResponseSchema>;

export const callStatusResponseSchema = z.object({
  exists: z.boolean(),
  room: z
    .object({
      name: z.string(),
      numParticipants: z.number(),
      creationTime: z.number(),
    })
    .nullable(),
  participants: z.array(
    z.object({
      identity: z.string(),
      name: z.string().optional(),
      joinedAt: z.number(),
    }),
  ),
});

export type CallStatusResponse = z.infer<typeof callStatusResponseSchema>;

export interface MuteParticipantInput {
  participantIdentity: string;
  trackSid: string;
  muted: boolean;
}

export interface KickParticipantInput {
  participantIdentity: string;
}

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[videoCallService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const videoCallService = {
  async joinCall(sessionId: string): Promise<JoinCallResponse> {
    const { data } = await realtimeClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/call/join`,
    );
    return parseOrThrow(joinCallResponseSchema, data.data, 'videoCallService.joinCall');
  },

  async endCall(sessionId: string): Promise<void> {
    await realtimeClient.post(`/sessions/${sessionId}/call/end`);
  },

  async startRecording(sessionId: string): Promise<StartRecordingResponse> {
    const { data } = await realtimeClient.post<{ data: unknown }>(
      `/sessions/${sessionId}/call/recording/start`,
    );
    return parseOrThrow(startRecordingResponseSchema, data.data, 'videoCallService.startRecording');
  },

  async stopRecording(sessionId: string): Promise<void> {
    await realtimeClient.post(`/sessions/${sessionId}/call/recording/stop`);
  },

  async muteParticipant(sessionId: string, input: MuteParticipantInput): Promise<void> {
    await realtimeClient.post(`/sessions/${sessionId}/call/mute`, input);
  },

  async kickParticipant(sessionId: string, input: KickParticipantInput): Promise<void> {
    await realtimeClient.post(`/sessions/${sessionId}/call/kick`, input);
  },

  async getCallStatus(sessionId: string): Promise<CallStatusResponse> {
    const { data } = await realtimeClient.get<{ data: unknown }>(
      `/sessions/${sessionId}/call/status`,
    );
    return parseOrThrow(callStatusResponseSchema, data.data, 'videoCallService.getCallStatus');
  },
} as const;
